import { MapPin, Phone, MessageCircle, Clock } from "lucide-react";
import { Seo } from "@/components/Seo";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { BookingForm } from "@/components/BookingForm";
import { BRAND, waLink } from "@/data/site";

const MAP_EMBED =
  "https://maps.google.com/maps?q=Pipe%20Line%20Road%2C%20Rajender%20Nagar%2C%20Manikonda%2C%20Hyderabad%2C%20Telangana%20500089&t=&z=15&ie=UTF8&iwloc=&output=embed";

export default function Contact() {
  return (
    <>
      <Seo
        title="Contact, Booking & Directions | Ody Luxurious Spa, Manikonda"
        description="Book a session at Ody Luxurious Spa, Pipe Line Road, beside RajShree Jewellers, Manikonda, Hyderabad 500089. Call or WhatsApp +91 90147 29756."
      />
      <PageHero
        eyebrow="Contact & Location"
        title="Two minutes from a quieter hour"
        subtitle="Book below, call, or simply walk in — we are beside RajShree Jewellers on Pipe Line Road, Manikonda."
        crumbs={[{ label: "Contact" }]}
      />
      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-10">
          <div className="space-y-5">
            <Reveal>
              <div className="rounded-3xl border border-bronze/10 bg-cream p-8">
                <span className="inline-flex rounded-full bg-bronze/10 p-3 text-bronze">
                  <MapPin size={18} />
                </span>
                <h2 className="mt-4 font-serif text-xl text-ink">Visit us</h2>
                <p data-testid="contact-address" className="mt-3 text-sm leading-relaxed text-stone2">
                  {BRAND.address}
                </p>
                <a
                  href={BRAND.maps}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-directions-btn"
                  className="mt-5 inline-flex items-center gap-2 text-[0.7rem] uppercase tracking-[0.2em] text-bronze hover:text-ink transition-colors duration-300"
                >
                  <MapPin size={13} /> Open in Google Maps
                </a>
              </div>
            </Reveal>
            <div className="grid sm:grid-cols-2 gap-5">
              <Reveal delay={0.1}>
                <a
                  href={BRAND.tel}
                  data-testid="contact-call-card"
                  className="block h-full rounded-3xl border border-bronze/10 bg-cream p-8 transition-[transform,border-color] duration-500 hover:-translate-y-1 hover:border-bronze/30"
                >
                  <span className="inline-flex rounded-full bg-bronze/10 p-3 text-bronze">
                    <Phone size={18} />
                  </span>
                  <h2 className="mt-4 font-serif text-xl text-ink">Call us</h2>
                  <p className="mt-2 text-sm text-bronze font-medium">{BRAND.phoneDisplay}</p>
                  <p className="mt-2 text-xs text-stone2">Tap to call directly</p>
                </a>
              </Reveal>
              <Reveal delay={0.18}>
                <a
                  href={waLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-whatsapp-card"
                  className="block h-full rounded-3xl border border-bronze/10 bg-cream p-8 transition-[transform,border-color] duration-500 hover:-translate-y-1 hover:border-bronze/30"
                >
                  <span className="inline-flex rounded-full bg-wa/15 p-3 text-wa-dark">
                    <MessageCircle size={18} />
                  </span>
                  <h2 className="mt-4 font-serif text-xl text-ink">WhatsApp</h2>
                  <p className="mt-2 text-sm text-wa-dark font-medium">Chat instantly</p>
                  <p className="mt-2 text-xs text-stone2">Replies within minutes</p>
                </a>
              </Reveal>
            </div>
            <Reveal delay={0.25}>
              <div className="rounded-3xl border border-bronze/10 bg-cream p-8 flex items-start gap-4">
                <span className="inline-flex rounded-full bg-bronze/10 p-3 text-bronze">
                  <Clock size={18} />
                </span>
                <div>
                  <h2 className="font-serif text-xl text-ink">Hours</h2>
                  <p className="mt-2 text-sm text-stone2">
                    Open all days of the week. Call or WhatsApp for today's timings and same-day
                    availability.
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <div id="booking">
              <BookingForm />
            </div>
          </Reveal>
        </div>
      </section>
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <div className="rounded-3xl overflow-hidden border border-bronze/10 bg-alabaster">
              <iframe
                data-testid="contact-map"
                title="Ody Luxurious Spa location map — Manikonda, Hyderabad"
                src={MAP_EMBED}
                className="w-full h-[380px] sm:h-[440px] grayscale-[35%] contrast-[1.02]"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
